<script setup>
import ExamplePromptChips from './ExamplePromptChips.vue';
import { useTranslations } from '../../composables/useTranslations.js';
import { inject } from 'vue';

const config = inject('aia.config');
const { t } = useTranslations(config?.locale);

defineEmits(['select']);

const prompts = config?.examplePrompts?.length ? config.examplePrompts : t('examplePrompts');
</script>

<template>
    <div class="aia-welcome flex flex-col items-start gap-4 py-6">
        <div>
            <p class="aia-welcome-greeting text-base font-medium">{{ t('welcome.greeting') }}</p>
            <p class="mt-1 text-sm text-[var(--muted-foreground)]">{{ t('welcome.hint') }}</p>
        </div>
        <ExamplePromptChips :prompts="prompts" @select="$emit('select', $event)" />
    </div>
</template>

<style scoped>
/* Gradient-text headline using the accent AI spectrum.
 * Falls back to plain foreground color on browsers without background-clip:text. */
.aia-welcome-greeting {
    /* Use a text-safe gradient (light stops only) when a variant defines one,
     * otherwise fall back to the accent gradient. Prevents near-black stops in
     * dark variants from making the clipped text invisible. */
    background-image: var(--aia-gradient-ai-text, var(--aia-gradient-ai-accent));
    background-size: 200% 200%;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
    -webkit-text-fill-color: transparent;
    animation: aia-welcome-shift 8s ease-in-out infinite;
}
@keyframes aia-welcome-shift {
    0%, 100% { background-position:   0% 50%; }
    50%      { background-position: 100% 50%; }
}

/* Stagger the chips in after the greeting */
.aia-welcome > * {
    animation: aia-welcome-in 360ms cubic-bezier(0.2, 0.65, 0.25, 1) backwards;
}
.aia-welcome > *:nth-child(1) { animation-delay: 60ms; }
.aia-welcome > *:nth-child(2) { animation-delay: 180ms; }

@keyframes aia-welcome-in {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
    .aia-welcome-greeting { animation: none; }
    .aia-welcome > * { animation: none; }
}
</style>
